/**
 * CreateActionCard.helpers — pure helper functions and entity content
 * registry for CreateActionCard.
 *
 * The ENTITY_CONTENT_REGISTRY maps entity type names (e.g. "ahoy_contact")
 * to a content component + props builder, so CreateActionCard can render
 * the right card via a simple lookup instead of inline conditionals.
 */

/* eslint-disable react-refresh/only-export-components -- helpers file with mixed exports (registry + utilities + content components) */
import { Calendar } from '@untitledui/icons';
import type { ComponentType } from 'react';
import { useMemo } from 'react';

import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import { CompactCardLayout } from '@/lib/crm/object-cards/CompactCardLayout';
import type { CompanyCardContentProps } from '@/lib/crm/object-cards/CompanyCardContent';
import { CompanyCardContent } from '@/lib/crm/object-cards/CompanyCardContent';
import type { ContactCardContentProps } from '@/lib/crm/object-cards/ContactCardContent';
import { ContactCardContent } from '@/lib/crm/object-cards/ContactCardContent';
import type { DealCardContentProps } from '@/lib/crm/object-cards/DealCardContent';
import { DealCardContent } from '@/lib/crm/object-cards/DealCardContent';
import { formatCloseDateString } from '@/lib/crm/object-cards/formatCloseDate';
import type { MetadataItem } from '@/lib/crm/object-cards/MetadataRow';
import { MetadataRow } from '@/lib/crm/object-cards/MetadataRow';
import type { RiskCardContentProps } from '@/lib/crm/object-cards/RiskCardContent';
import { RiskCardContent } from '@/lib/crm/object-cards/RiskCardContent';
import { hasMeaningfulTitle } from '@/lib/crm/record/notes/noteKind';
import { formatMaybeMultiValue } from '@/lib/crm/utils/formatMultiValue';
import { getEntityTypeIconComponent } from '@/lib/crm/utils/icons/EntityTypeIcon';
import { getPropertyTypeIconComponent } from '@/lib/crm/utils/icons/PropertyTypeIcon';
import type { CrmObject } from '@/types/crm';

import type { ActionRendererProps } from '../../lib/proposals/registry/types';
import { extractValue } from './shared';

// ─── Property helpers ────────────────────────────────────────────────────────

export function extractProperties(
  properties: Record<string, unknown>
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(properties)) {
    result[key] = extractValue(val);
  }
  return result;
}

export function wrapProperties(
  flat: Record<string, unknown>
): Record<string, { value: unknown }> {
  const result: Record<string, { value: unknown }> = {};
  for (const [key, val] of Object.entries(flat)) {
    result[key] = { value: val };
  }
  return result;
}

export function formatValue(v: unknown): string {
  if (v === null || v === undefined) {
    return '';
  }
  if (typeof v === 'string') {
    return v;
  }
  if (typeof v === 'number' || typeof v === 'boolean') {
    return String(v);
  }
  if (Array.isArray(v)) {
    return formatMaybeMultiValue(v);
  }
  return JSON.stringify(v);
}

/**
 * Format a currency/amount value for display.
 * Handles both raw numbers/strings and {amount, unit} objects from CRM.
 */
function formatCurrencyAmount(v: unknown): string {
  if (v === null || v === undefined) {
    return '';
  }
  // Handle {amount, unit} currency objects from CRM
  if (typeof v === 'object' && v !== null && 'amount' in (v as object)) {
    const obj = v as { amount: string | number; unit?: string };
    const num =
      typeof obj.amount === 'string' ? parseFloat(obj.amount) : obj.amount;
    if (isNaN(num)) {
      return '';
    }
    if (obj.unit) {
      try {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: obj.unit,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(num);
      } catch {
        return `${num.toLocaleString()} ${obj.unit}`;
      }
    }
    return num.toLocaleString();
  }
  // Raw number or numeric string — format as USD (codebase default)
  if (typeof v === 'number' || typeof v === 'string') {
    if (typeof v === 'string' && v.trim() === '') {
      return '';
    }
    const num = typeof v === 'string' ? Number(v) : v;
    if (Number.isFinite(num)) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(num);
    }
    return '';
  }
  return formatValue(v);
}

// ─── Typed content props builders ────────────────────────────────────────────

/** Build ContactCardContentProps from flat properties + enrichedData.
 *
 * flatProps takes priority over enrichedData for the name because flatProps
 * comes from the CRM store (real-time, WebSocket-updated) while enrichedData
 * is a static snapshot from when the proposal was created.
 */
function buildContactContentProps(
  flatProps: Record<string, unknown>,
  enrichedData: ActionRendererProps['enrichedData']
): Omit<ContactCardContentProps, 'compact' | 'onClick' | 'entityId' | 'orgId'> {
  // Returns `undefined` for any field we don't have from the payload /
  // enrichedData. When the content card is given an entityId (from
  // ActionEntityPreview), it'll self-hydrate the missing values from the
  // CRM store and render per-field skeletons until those values arrive —
  // empty strings here would read as "the value is empty" and bypass
  // that loading behavior.
  const derivedName =
    [
      formatValue(flatProps.ahoy_first_name),
      formatValue(flatProps.ahoy_last_name),
    ]
      .filter(Boolean)
      .join(' ') || undefined;

  return {
    name: derivedName ?? enrichedData?.object_label ?? undefined,
    email: formatValue(flatProps.ahoy_email) || undefined,
    jobTitle: formatValue(flatProps.ahoy_job_title) || undefined,
    location:
      formatValue(flatProps.ahoy_location) ||
      formatValue(flatProps.ahoy_city) ||
      undefined,
    companyName:
      formatValue(flatProps.ahoy_company_name) ||
      enrichedData?.target_object?.label ||
      undefined,
    linkedInUrl: formatValue(flatProps.ahoy_linkedin_url) || undefined,
  };
}

/** Build CompanyCardContentProps from flat properties + enrichedData.
 *
 * flatProps takes priority over enrichedData for the name — see
 * buildContactContentProps comment for rationale.
 */
function buildCompanyContentProps(
  flatProps: Record<string, unknown>,
  enrichedData: ActionRendererProps['enrichedData']
): CompanyCardContentProps {
  return {
    name:
      formatValue(flatProps.ahoy_company_name) ||
      formatValue(flatProps.ahoy_name) ||
      enrichedData?.object_label ||
      'New Company',
    industry: formatValue(flatProps.ahoy_industry) || undefined,
    headcount:
      formatValue(flatProps.ahoy_headcount_range) ||
      formatValue(flatProps.ahoy_employee_count) ||
      undefined,
    location:
      formatValue(flatProps.ahoy_location) ||
      formatValue(flatProps.ahoy_city) ||
      undefined,
  };
}

/**
 * Build DealCardContentProps from flat properties + enrichedData.
 *
 * The `enrichedData.target_object` field is populated with the associated company
 * when a "Create Deal + Associate Company" merged card is rendered:
 * MergedCreateActionCard merges the company association's enriched data into
 * target_object before passing enrichedData here, so no extra logic is needed.
 */
export function buildDealContentProps(
  flatProps: Record<string, unknown>,
  enrichedData: ActionRendererProps['enrichedData'],
  context?: {
    resolvedTargets?: Record<string, CrmObject>;
    pendingTargetTypes?: Set<string>;
    orgId?: number;
    nestedAssociations?: 'card' | 'chip';
  }
): Omit<DealCardContentProps, 'compact' | 'onClick' | 'entityId' | 'orgId'> {
  const amount = formatCurrencyAmount(flatProps.ahoy_amount);
  // A YYYY-MM-DD close date renders as "Oct 20, 2026" (the same formatter
  // the store-fed card uses); anything else passes through as typed.
  const rawCloseDate = formatValue(flatProps.ahoy_close_date);
  const closeDate = formatCloseDateString(rawCloseDate) ?? rawCloseDate;

  // Build the nested company sub-card. The self-hydrating
  // CompanyCardContent will handle its own per-field loading when given
  // an entityId, so we just need to point it at the right one.
  //
  //   1. Fully-resolved CrmObject in resolvedTargets → pass its entity_id;
  //      the card reads industry / headcount / location from the store.
  //   2. No resolved target but the deal has a company association in the
  //      store (pendingTargetTypes) → we don't have the entity_id at this
  //      layer, so fall through to a name-only scalar sub-card using
  //      whatever `ahoy_company_name` was injected into flatProps by
  //      ActionEntityPreview's scalar-name projection. The metadata row
  //      shows empty placeholders (not skeletons) in that narrow case.
  //   3. Neither — no sub-card.
  const resolvedCompany = context?.resolvedTargets?.ahoy_company;
  let company: DealCardContentProps['company'];
  if (resolvedCompany) {
    company = { entityId: resolvedCompany.entity_id, orgId: context?.orgId };
  } else {
    // Fall back to enrichedData.target_object.label so create-deal cards
    // still render an associated company sub-card when the resolved target
    // hasn't hydrated yet (e.g. MergedCreateActionCard's nested company
    // association that the backend enriched but the store hasn't fetched).
    const companyName =
      formatValue(flatProps.ahoy_company_name) ||
      enrichedData?.target_object?.label ||
      undefined;
    if (companyName) {
      company = { name: companyName };
    }
  }

  return {
    name:
      formatValue(flatProps.ahoy_deal_name) ||
      formatValue(flatProps.ahoy_name) ||
      enrichedData?.object_label ||
      'New Deal',
    totalAmount: amount || undefined,
    closeDate: closeDate || undefined,
    company,
    nestedCompany: context?.nestedAssociations,
  };
}

/**
 * Build RiskCardContentProps from flat properties + enrichedData.
 *
 * The risk title comes from flatProps (live store data). The associated
 * entity label comes from enrichedData.target_object (populated by
 * MergedCreateActionCard for merged associations).
 */
function buildRiskContentProps(
  flatProps: Record<string, unknown>,
  enrichedData: ActionRendererProps['enrichedData']
): RiskCardContentProps {
  const associatedLabel = enrichedData?.target_object?.label || undefined;
  const associatedType = enrichedData?.target_object?.entity_type_name;

  // Derive a human-readable badge label from the associated entity type
  let associatedEntityType: string | undefined;
  if (associatedType) {
    const typeSuffix = associatedType.replace('ahoy_', '');
    associatedEntityType = `${typeSuffix.charAt(0).toUpperCase()}${typeSuffix.slice(1)} update`;
  }

  return {
    title:
      formatValue(flatProps.ahoy_title) ||
      enrichedData?.object_label ||
      'New Risk',
    description: formatValue(flatProps.ahoy_description) || undefined,
    associatedEntityLabel: associatedLabel,
    associatedEntityType,
    severity: formatValue(flatProps.ahoy_severity) || undefined,
    category: formatValue(flatProps.ahoy_risk_category) || undefined,
    status: formatValue(flatProps.ahoy_status) || undefined,
  };
}

// ─── Entity content registry ────────────────────────────────────────────────

/**
 * Props for entity content components in the registry.
 * Every registered component accepts these and maps them internally
 * to the underlying card content component's props.
 */
export interface EntityContentComponentProps {
  /**
   * CRM entity UUID. When provided, the underlying content component
   * (e.g. ContactCardContent) subscribes to the CRM store and resolves
   * its own fields — each rendering a skeleton until its data arrives.
   * Prop-based values produced by the `flatProps` builder below still
   * win as explicit overrides when defined.
   */
  entityId?: string;
  orgId?: number;

  /** Props derived from the action payload — pass-through to the underlying card. */
  flatProps: Record<string, unknown>;
  enrichedData: ActionRendererProps['enrichedData'];
  /** Optional click handler — passed through to the card content's onClick */
  onClick?: () => void;
  /**
   * Render a compact chip (avatar + name + entity-type pill) instead of
   * the full card with subtitle/metadata/badges. Used by Link and Merge
   * action cards where two entities sit side-by-side in a LinkedPair and
   * the full layout would crowd the row.
   */
  compact?: boolean;
  /**
   * Omit the entity-type pill when the surrounding context already names
   * the type. Honored in both compact and full mode — a full card can
   * auto-collapse to the compact layout and would otherwise regain the
   * pill, so callers that want it hidden pass it unconditionally.
   */
  hideTypeBadge?: boolean;
  /**
   * How the full card renders associated records: nested sub-cards
   * (`card`, default) or an inline badge in the subtitle line (`chip`).
   * Forwarded to builders that synthesise nested-card props.
   */
  nestedAssociations?: 'card' | 'chip';
  /**
   * Fully-resolved CRM objects for any associations on the source entity,
   * keyed by their `entity_type_name`. Used by builders that still need
   * to synthesise nested sub-card props upfront (e.g. DealCardContent's
   * company card). New self-hydrating cards ignore this and read their
   * own associations from the store via their own entityId.
   */
  resolvedTargets?: Record<string, CrmObject>;
  /**
   * Set of target entity types the source has an association for but whose
   * CrmObjects haven't yet been fetched into the store. Same backwards-
   * compat story as `resolvedTargets` — self-hydrating cards derive their
   * own pending state from the store.
   */
  pendingTargetTypes?: Set<string>;
}

/**
 * HOC that creates a registry-compatible component from a card content
 * component and its props builder. The props builder can opt into nested
 * sub-card data by accepting the optional third `resolvedTargets` argument.
 */
function createEntityContent<
  P extends {
    entityId?: string;
    orgId?: number;
    compact?: boolean;
    hideTypeBadge?: boolean;
    onClick?: () => void;
  },
>(
  Component: ComponentType<P>,
  buildProps: (
    flatProps: Record<string, unknown>,
    enrichedData: ActionRendererProps['enrichedData'],
    context?: {
      resolvedTargets?: Record<string, CrmObject>;
      pendingTargetTypes?: Set<string>;
      orgId?: number;
      nestedAssociations?: 'card' | 'chip';
    }
  ) => Omit<P, 'compact' | 'hideTypeBadge' | 'onClick' | 'entityId' | 'orgId'>
): ComponentType<EntityContentComponentProps> {
  function EntityContent({
    entityId,
    orgId,
    flatProps,
    enrichedData,
    onClick,
    compact,
    hideTypeBadge,
    nestedAssociations,
    resolvedTargets,
    pendingTargetTypes,
  }: EntityContentComponentProps) {
    const props = buildProps(flatProps, enrichedData, {
      resolvedTargets,
      pendingTargetTypes,
      orgId,
      nestedAssociations,
    });
    // Compact chips (Link / Merge / homogeneous table previews) don't render
    // the full card body, so the CRM-store subscription the underlying
    // content component would kick off via `entityId` is wasted work — a
    // fresh fetch subscription per compact row blows up the store request
    // fan-out. Suppress entityId/orgId in compact mode so compact rows stay
    // purely presentational and only full-card renders self-hydrate.
    return (
      <Component
        {...(props as unknown as P)}
        {...(!compact && entityId !== undefined ? { entityId } : {})}
        {...(!compact && orgId !== undefined ? { orgId } : {})}
        {...(onClick ? { onClick } : {})}
        {...(compact ? { compact: true } : {})}
        // Not gated on `compact`: a full card can collapse to the compact
        // layout on its own when it has no secondary fields, and would pick
        // the type pill back up unless the caller's request to hide it
        // reaches the component in full mode too.
        {...(hideTypeBadge ? { hideTypeBadge: true } : {})}
      />
    );
  }
  EntityContent.displayName = `EntityContent(${Component.displayName || Component.name})`;
  return EntityContent;
}

// Note: `ahoy_date` is stored as a YYYY-MM-DD calendar date (not a datetime).
// Parsing via `new Date(yyyyMmDd)` would interpret it as UTC midnight and shift
// it a day for some local timezones — so split-and-construct keeps it in the
// user's local calendar where the writer intended it.
function parseCalendarDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    return null;
  }
  const [, year, month, day] = match;
  const y = Number(year);
  const m = Number(month);
  const dayNum = Number(day);
  const d = new Date(y, m - 1, dayNum);
  // Reject out-of-range day/month inputs (e.g. 2026-02-30) — `Date` would
  // otherwise silently roll them over to a different calendar date.
  if (
    d.getFullYear() !== y ||
    d.getMonth() !== m - 1 ||
    d.getDate() !== dayNum
  ) {
    return null;
  }
  return d;
}

const NOTE_DATE_FMT_DAY = { month: 'short', day: 'numeric' } as const;
const NOTE_DATE_FMT_FULL = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
} as const;

function formatNoteDate(d: Date): string {
  const showYear = d.getFullYear() !== new Date().getFullYear();
  return d.toLocaleDateString(
    'en-US',
    showYear ? NOTE_DATE_FMT_FULL : NOTE_DATE_FMT_DAY
  );
}

/**
 * Note card content — renders the markdown body of an ahoy_note create
 * action. Notes don't need the self-hydrating `entityId` plumbing the
 * other entity types use because the underlying note doesn't exist yet
 * (we're previewing what's about to be written), so this is a
 * standalone presentational component instead of a createEntityContent()
 * HOC wrap.
 *
 * Layout:
 * - Note title: rendered as an <h4> heading when the note has a
 *   meaningful title (non-empty after trim, and not the legacy "Note"
 *   placeholder, case-insensitive), mirroring the canonical NoteCard
 *   renderer. Preview paths reach this component via ActionEntityPreview
 *   (trigger / delete / update / hidden / stage-association cards) with
 *   no parent title header, so the title must render here — otherwise a
 *   title-only note (empty body) would show only "Empty note".
 * - Date (📅 …) is shown only when `ahoy_date` is set. When the
 *   note has a meaningful title, the date appears on the same row
 *   as the title (right-aligned via `justify-between`). When there
 *   is no meaningful title, the date renders on its own right-aligned
 *   row (the original layout).
 * - Body: markdown content rendered via MarkdownContent (no truncation —
 *   long notes expand the card and the parent accordion handles scroll).
 */
function NoteLikeCardContent({
  flatProps,
  enrichedData,
  compact,
  onClick,
  orgId,
  hideTypeBadge,
  entityTypeName,
  noun,
}: EntityContentComponentProps & {
  entityTypeName: 'ahoy_note' | 'ahoy_task';
  /** Capitalised singular noun for fallbacks ("Note" / "Task"). */
  noun: string;
}) {
  // Compact mode is used by LinkActionCard / MergeActionCard when a note
  // sits next to another entity in a LinkedPair. Match the chip shape
  // used by Contact / Company / Deal so the note renders as name + Note
  // entity-type pill underneath, with the same click affordance.
  if (compact) {
    const name =
      formatValue(flatProps['ahoy_title']) ||
      enrichedData?.object_label ||
      noun;
    return (
      <CompactCardLayout
        name={name}
        entityTypeName={entityTypeName}
        onClick={onClick}
        hideTypeBadge={hideTypeBadge}
      />
    );
  }

  const content = formatValue(flatProps['ahoy_content']);
  const title = formatValue(flatProps['ahoy_title']);
  // Same "meaningful title" rule NoteCard uses (empty OR the legacy "Note"
  // placeholder = no title) — one home in noteKind.ts so surfaces can't drift.
  const meaningfulTitle = hasMeaningfulTitle(title);
  const rawDate =
    entityTypeName === 'ahoy_task' ? formatValue(flatProps['ahoy_date']) : '';
  const parsedDate = rawDate ? parseCalendarDate(rawDate) : null;
  const dateLabel = parsedDate ? formatNoteDate(parsedDate) : '';

  const dateChip = dateLabel ? (
    <div className='flex shrink-0 items-center gap-1 text-xs text-tertiary'>
      <Calendar className='w-3.5 h-3.5' aria-hidden='true' />
      <span>{dateLabel}</span>
    </div>
  ) : null;

  return (
    <div className='space-y-2'>
      {meaningfulTitle ? (
        <div className='flex items-start justify-between gap-2'>
          <h4 className='min-w-0 text-sm font-semibold text-fg-primary'>
            {title}
          </h4>
          {dateChip}
        </div>
      ) : (
        dateChip && <div className='flex justify-end'>{dateChip}</div>
      )}
      {content && (
        <MarkdownContent
          className='text-sm text-fg-secondary'
          content={content}
          orgId={orgId}
          size='compact'
        />
      )}
      {!meaningfulTitle && !content && (
        <div className='text-sm italic text-tertiary'>
          Empty {noun.toLowerCase()}
        </div>
      )}
    </div>
  );
}

/** ahoy_note create-action content (see NoteLikeCardContent). */
function NoteCardContent(props: EntityContentComponentProps) {
  return (
    <NoteLikeCardContent {...props} entityTypeName='ahoy_note' noun='Note' />
  );
}

/**
 * ahoy_task create-action content — a task is a note-like record (same memo
 * contract), so it previews exactly as a note does, under its own type pill.
 */
function TaskCardContent(props: EntityContentComponentProps) {
  return (
    <NoteLikeCardContent {...props} entityTypeName='ahoy_task' noun='Task' />
  );
}

// ─── Property Type card ────────────────────────────────────────────────────

/**
 * Static field-type label map for proposal cards. We can't call the schema
 * API from an action card (these render without a schema query), so we
 * maintain a lightweight local mapping for presentation only.
 */
const FIELD_TYPE_LABELS: Record<string, string> = {
  text: 'Text',
  textarea: 'Long Text',
  markdown: 'Rich Text',
  number: 'Number',
  percentage: 'Percentage',
  currency: 'Currency',
  date: 'Date',
  datetime: 'Date & Time',
  email: 'Email',
  phone_number: 'Phone',
  url: 'URL',
  domain: 'Domain',
  select: 'Select',
  single_select: 'Single Select',
  multi_select: 'Multi Select',
};

export function formatFieldTypeLabel(fieldTypeName: string): string {
  return (
    FIELD_TYPE_LABELS[fieldTypeName] ??
    fieldTypeName
      .split('_')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
  );
}

/**
 * PropertyTypeCardContent — renders the card body for "create a new custom
 * property" actions (entity_type_name = ahoy_property_type).
 *
 * Layout:
 * - Top row: field-type icon + property label (e.g. [TextIcon] Lead Source)
 * - Metadata row: field type name + target entity type (e.g. "Text · Contact")
 * - Optional: description text
 * - Optional: options list for select fields
 *
 * Purely presentational — the property type doesn't exist yet (we're
 * previewing what will be created), so there's no self-hydrating store
 * subscription.
 */
function PropertyTypeCardContent({
  flatProps,
  enrichedData,
  compact,
  onClick,
  hideTypeBadge,
}: EntityContentComponentProps) {
  const label =
    formatValue(flatProps['ahoy_label']) ||
    enrichedData?.object_label ||
    'New Property';
  const fieldTypeName = formatValue(flatProps['ahoy_field_type_name']);
  const description = formatValue(flatProps['ahoy_description']);

  // Resolve options — handle both array-of-objects and plain arrays.
  // Each option carries a stable, guaranteed-unique `key`: duplicate labels
  // are disambiguated with an occurrence counter so React keys never collide
  // (and without using the array index as the key).
  const rawOptions = flatProps['ahoy_options'];
  const options: { label: string; key: string }[] = useMemo(() => {
    if (!Array.isArray(rawOptions)) {
      return [];
    }
    const seen = new Map<string, number>();
    return rawOptions
      .map((opt: unknown): string => {
        if (typeof opt === 'string') {
          return opt;
        }
        if (typeof opt === 'object' && opt !== null && 'label' in opt) {
          return String((opt as { label: unknown }).label);
        }
        return '';
      })
      .filter(Boolean)
      .map((label: string) => {
        const occurrence = seen.get(label) ?? 0;
        seen.set(label, occurrence + 1);
        return {
          label,
          key: occurrence === 0 ? label : `${label}-${occurrence}`,
        };
      });
  }, [rawOptions]);

  const FieldTypeIcon = fieldTypeName
    ? getPropertyTypeIconComponent({ field_type_name: fieldTypeName })
    : null;

  const fieldTypeLabel = fieldTypeName
    ? formatFieldTypeLabel(fieldTypeName)
    : '';

  // Entity type is already shown in the accordion title ("Create new
  // contact property"), so we only show the field type in the card body.
  const metadataItems: MetadataItem[] = useMemo(() => {
    const items: MetadataItem[] = [];
    if (fieldTypeLabel) {
      items.push({ key: 'field-type', label: fieldTypeLabel });
    }
    return items;
  }, [fieldTypeLabel]);

  if (compact) {
    return (
      <CompactCardLayout
        name={label}
        entityTypeName='ahoy_property_type'
        onClick={onClick}
        hideTypeBadge={hideTypeBadge}
      />
    );
  }

  return (
    <div className='space-y-1.5'>
      {/* Top row: field-type icon + property name */}
      <div className='flex items-center gap-2'>
        {FieldTypeIcon && (
          <FieldTypeIcon
            className='w-4 h-4 text-fg-secondary shrink-0'
            aria-hidden='true'
          />
        )}
        <span className='text-sm font-medium text-fg-primary truncate'>
          {label}
        </span>
      </div>

      {/* Metadata: field type + target entity type */}
      {metadataItems.length > 0 && (
        <MetadataRow
          items={metadataItems}
          className={FieldTypeIcon ? 'pl-6' : ''}
        />
      )}

      {/* Description */}
      {description && (
        <p
          className={`text-xs text-fg-tertiary ${FieldTypeIcon ? 'pl-6' : ''}`}
        >
          {description}
        </p>
      )}

      {/* Options for select fields */}
      {options.length > 0 && (
        <div className={`flex flex-wrap gap-1 ${FieldTypeIcon ? 'pl-6' : ''}`}>
          {options.map(opt => (
            <span
              key={opt.key}
              className='inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-bg-tertiary text-fg-secondary'
            >
              {opt.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Entity Type card ─────────────────────────────────────────────────────

/**
 * EntityTypeCardContent — renders the card body for "create a new custom
 * entity type" actions (entity_type_name = ahoy_entity_type).
 *
 * Layout:
 * - Top row: entity icon (resolved from ahoy_icon) + display name
 * - Metadata row: plural name
 * - Optional: description text
 *
 * Purely presentational — the entity type doesn't exist yet (we're
 * previewing what will be created), so there's no self-hydrating store
 * subscription.
 */
function EntityTypeCardContent({
  flatProps,
  enrichedData,
  compact,
  onClick,
  hideTypeBadge,
}: EntityContentComponentProps) {
  const displayName =
    formatValue(flatProps['ahoy_display_name']) ||
    enrichedData?.object_label ||
    'New object type';
  const pluralName = formatValue(flatProps['ahoy_display_name_plural']);
  const description = formatValue(flatProps['ahoy_description']);
  const iconName = formatValue(flatProps['ahoy_icon']);

  const EntityIcon = iconName
    ? getEntityTypeIconComponent('ahoy_entity_type', iconName)
    : getEntityTypeIconComponent('ahoy_entity_type');

  const metadataItems: MetadataItem[] = useMemo(() => {
    const items: MetadataItem[] = [];
    if (pluralName) {
      items.push({ key: 'plural', label: `Plural: ${pluralName}` });
    }
    return items;
  }, [pluralName]);

  if (compact) {
    return (
      <CompactCardLayout
        name={displayName}
        entityTypeName='ahoy_entity_type'
        onClick={onClick}
        hideTypeBadge={hideTypeBadge}
      />
    );
  }

  return (
    <div className='space-y-1.5'>
      {/* Top row: entity icon + display name */}
      <div className='flex items-center gap-2'>
        <EntityIcon
          className='w-4 h-4 text-fg-secondary shrink-0'
          aria-hidden='true'
        />
        <span className='text-sm font-medium text-fg-primary truncate'>
          {displayName}
        </span>
      </div>

      {/* Metadata: plural name */}
      {metadataItems.length > 0 && (
        <MetadataRow items={metadataItems} className='pl-6' />
      )}

      {/* Description */}
      {description && (
        <p className='text-xs text-fg-tertiary pl-6'>{description}</p>
      )}
    </div>
  );
}

/**
 * Entity type → content component.
 *
 * To add a new entity type card, add an entry here with the component
 * and a props builder. CreateActionCard will pick it up automatically —
 * no conditionals needed.
 */
export const ENTITY_CONTENT_REGISTRY: Record<
  string,
  ComponentType<EntityContentComponentProps>
> = {
  ahoy_contact: createEntityContent(
    ContactCardContent,
    buildContactContentProps
  ),
  ahoy_company: createEntityContent(
    CompanyCardContent,
    buildCompanyContentProps
  ),
  ahoy_deal: createEntityContent(DealCardContent, buildDealContentProps),
  ahoy_risk: createEntityContent(RiskCardContent, buildRiskContentProps),
  ahoy_note: NoteCardContent,
  ahoy_task: TaskCardContent,
  ahoy_property_type: PropertyTypeCardContent,
  ahoy_entity_type: EntityTypeCardContent,
};
